import { memo, useMemo, type ComponentProps } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { AnnotationSet } from "../../api/threads/liveMessages";
import { formatSegmentRef, parseDocumentVersionRef } from "../../shared/references/contentReferences";
import { appPaths } from "../../shared/routing/appPaths";
import CopyReference from "../../ui/content/CopyReference";
import LoadStatus from "../../ui/feedback/LoadStatus";
import ItemReferenceName from "../items/ItemReferenceName";
import DocumentVersionName from "../sources/document/DocumentVersionName";
import { segmentDisplayName } from "../sources/document/documentNames";
import AskAboutSegment from "../sources/document/parts/AskAboutSegment";
import { useSourceDocument } from "../sources/document/useSourceDocument";
import { NO_ANSWER_PROBLEMS, type AnswerProblems } from "./annotationEditing";
import type { AnnotationLabelOption } from "./annotationLabels";
import AnnotationTextEditor from "./AnnotationTextEditor";
import { documentOffsetsOf, groupByDocument, placeAnnotations, type AnnotatedSegment, type IndexedAnnotation } from "./annotationPlacement";
import UnplacedAnnotations from "./UnplacedAnnotations";
import type { AnnotationSetEditor } from "./useAnnotationSetEditing";
import { useLabelListNames } from "./useLabelListNames";

const NO_LABEL_NAMES: ReadonlyMap<string, string> = new Map();
const NO_LABEL_OPTIONS: AnnotationLabelOption[] = [];

interface AnnotationSetViewProps {
  readonly output: AnnotationSet;
  /** Edit the set, as a review does (W-11); without it, the view is read-only. */
  readonly editing?: AnnotationSetEditor;
  /** Why the server refused the edited set, shown on the spans they concern. */
  readonly problems?: AnswerProblems;
  /** Where selecting a segment's text asks the agent about it, by its segment reference (F96);
   * read-only views only, since the span editor uses the selection to add a span. */
  readonly askInProject?: string;
}

/**
 * An annotation set (W-6) on the text it annotates: each segment of the exact document
 * version, its spans in label colours with the label names, as v1's span view showed them.
 * Each document version loads, fails and retries on its own, through the session's
 * document cache; label names come separately, and labels show by key until they do.
 *
 * With `editing`, a review edits the set in v1's span editor: each span's `id` is its index
 * in the set, and `documentOffsetsOf` turns a selected span back into the server's
 * offsets. Every document of the set then shows, so spans can be added to any of them.
 */
export default memo(AnnotationSetView);

function AnnotationSetView({ output, editing, problems = NO_ANSWER_PROBLEMS, askInProject }: AnnotationSetViewProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupByDocument(output), [output]);
  // Without the label list (no access, or gone), labels show by their keys and the list by its ID.
  const names = useLabelListNames(output.label_list);

  return (
    <section className="space-y-2 text-sm">
      <p>
        <Trans
          i18nKey="threads.output.annotations"
          count={output.annotations.length}
          components={{
            document: <DocumentVersionName reference={output.document} />,
            labels: <ItemReferenceName reference={output.label_list} knownName={names?.listName ?? null} />,
          }}
        />
      </p>
      <details className="text-xs">
        <summary className="app-text-muted cursor-pointer">{t("threads.output.annotationDetails")}</summary>
        <dl className="mt-1 space-y-1">
          {[...groups.keys()].map((reference) => <ReferenceRow key={reference} term={t("threads.output.annotationDocument")} reference={reference} />)}
          <ReferenceRow term={t("threads.output.annotationLabelList")} reference={output.label_list} />
        </dl>
      </details>
      {problems.general.length === 0 ? null : (
        <ul className="app-error-text list-disc pl-5 text-xs">
          {problems.general.map((reason) => <li key={reason}>{reason}</li>)}
        </ul>
      )}
      {[...groups]
        // The set's document is named in the heading; read-only, its text shows only if annotations lie in it.
        .filter(([, annotations]) => editing !== undefined || annotations.length > 0 || groups.size === 1)
        .map(([reference, annotations]) => (
          <AnnotatedDocument
            key={reference}
            reference={reference}
            annotations={annotations}
            labelNames={names?.labels ?? NO_LABEL_NAMES}
            labelOptions={names?.options ?? NO_LABEL_OPTIONS}
            named={reference !== output.document}
            editing={editing}
            askInProject={editing === undefined ? askInProject : undefined}
            problems={problems.byIndex}
          />
        ))}
    </section>
  );
}

function ReferenceRow({ term, reference }: { term: string; reference: string }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2">
      <dt className="app-text-muted">{term}</dt>
      <dd className="flex min-w-0 items-center gap-1 break-all font-mono text-[11px]">
        {reference}
        <CopyReference value={reference} />
      </dd>
    </div>
  );
}

interface AnnotatedDocumentProps {
  reference: string;
  annotations: readonly IndexedAnnotation[];
  labelNames: ReadonlyMap<string, string>;
  labelOptions: AnnotationLabelOption[];
  /** Whether to name the document: when it is not the one the heading names (W-15). */
  named: boolean;
  editing: AnnotationSetEditor | undefined;
  askInProject: string | undefined;
  /** The server's reasons, by annotation index. */
  problems: ReadonlyMap<number, readonly string[]>;
}

function AnnotatedDocument({ reference, annotations, labelNames, labelOptions, named, editing, problems, askInProject }: AnnotatedDocumentProps) {
  const { t } = useTranslation();
  const version = parseDocumentVersionRef(reference);
  const { state, reload } = useSourceDocument(version);
  const document = state.status === "loaded" ? state.value : null;
  const placement = useMemo(() => {
    if (document === null) {
      return null;
    }
    const placed = placeAnnotations(document, annotations);
    if (problems.size === 0) {
      return placed;
    }
    const withProblems = (segment: AnnotatedSegment) => ({
      ...segment,
      annotations: segment.annotations.map((span) => ({ ...span, problems: problems.get(Number(span.id)) })),
    });
    return { ...placed, segments: placed.segments.map(withProblems) };
  }, [document, annotations, problems]);
  return (
    <div className="space-y-3">
      {named ? <h4 className="text-xs font-semibold"><DocumentVersionName reference={reference} /></h4> : null}
      <LoadStatus state={state} loadingLabel={t("threads.output.annotationsLoading")} loadingClassName="justify-start" onRetry={reload} compact />
      {placement?.segments.map((segment) => {
        const name = segment.segmentPath === "" ? null : segmentDisplayName(segment.segmentPath);
        // A span is added in a segment the server can name; a document without segments has none.
        const editProps: Partial<ComponentProps<typeof AnnotationTextEditor>> = editing === undefined || version === null || segment.segmentPath === "" ? {} : {
          editable: true,
          selectedId: editing.selectedId,
          onSelectAnnotation: editing.select,
          onUpdateAnnotationType: editing.relabel,
          onDeleteAnnotation: editing.remove,
          onAddAnnotation: (added) => {
            const offsets = documentOffsetsOf(segment, added);
            editing.add({ segment: formatSegmentRef(version, segment.segmentPath), ...offsets, quote: added.value, label: added.type });
          },
        };
        return (
          <div key={segment.segmentPath}>
            {name === null || version === null ? null : (
              <h4 className="mb-1 text-xs font-semibold">
                <Link to={appPaths.sourceDocument(version, segment.segmentPath)} title={`${reference}#${segment.segmentPath}`} className="app-link">{name}</Link>
              </h4>
            )}
            <AskAboutSegment projectId={askInProject} version={version} segmentPath={segment.segmentPath} name={name}>
              <AnnotationTextEditor
                text={segment.text}
                entities={segment.annotations}
                labelNames={labelNames}
                labelOptions={labelOptions}
                {...editProps}
                selectLabel={t("threads.output.annotationLabel")}
                addLabel={t("threads.output.annotationAdd")}
                deleteLabel={t("threads.output.annotationDelete")}
                annotationListLabel={name === null ? t("threads.output.annotationsInText") : t("threads.output.annotationsInSegment", { segment: name })}
              />
            </AskAboutSegment>
          </div>
        );
      })}
      {/* Without a text to show them in (still loading, unreadable, or no exact version), its annotations are listed. */}
      {state.status === "loading" ? null : <UnplacedAnnotations annotations={placement?.unplaced ?? annotations} labelNames={labelNames} problems={problems} />}
    </div>
  );
}
